import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  ADDITIONAL_OFFSET,
  CheckoutConfigurationType,
  LocalStorageKeys,
  QRCodeCheckoutStatus,
  ROUTES,
  SOCKET_MESSAGE_TYPE,
  TranslationContext,
} from "@is/constants";
import { ScannerContext, ViewStateContext } from "@is/contexts";
import {
  useCollections,
  useContentItems,
  useManageBasket,
  useManageCheckoutConfigurations,
  usePreloadMedia,
  useScanner,
  useScreenOrientation,
  useWebSocketConnect,
  useWebSocketWorkerMessage,
} from "@is/hooks";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { getQRCheckoutSessionId, markSessionFinished } from "@is/utils/session";

const BUTTONS_CONTAINER_TOP_DEFAULT = 40;
const BUTTONS_CONTAINER_TOP_SCROLLED = 34;
const BUTTON_HEIGHT = 60;
const BUTTONS_GAP = 16;

const SMOOTH_START_SCROLL_OPTIONS: ScrollIntoViewOptions = {
  behavior: "smooth",
  block: "start",
};

const SMOOTH_END_SCROLL_OPTIONS: ScrollIntoViewOptions = {
  behavior: "smooth",
  block: "end",
};

const useManageContentView = () => {
  const { t } = useTranslation(TranslationContext.ProductListing);
  const entryPageRoute = `${window.location.origin}/instore${ROUTES.EntryView}`;
  const { collections } = useCollections({ filterOutOfStockItems: false });

  const { connect } = useWebSocketConnect();
  const navigate = useNavigate();

  const [showBasketDrawer, setShowBasketDrawer] = useState(false);
  const [checkoutConfigurationsType, setCheckoutConfigurationsType] =
    useState<CheckoutConfigurationType>(null);
  const [searchDrawerHeight, setSearchDrawerHeight] = useState<number>(0);
  const [showGoToBottomButton, setShowGoToBottomButton] = useState(false);
  const { scannedValue } = useContext(ScannerContext);

  const {
    focusedItemId,
    preloadMediaItems,
    showBasketDrawer: showBasketDrawerContextValue,
    showLanguageAndCurrencyModal,
    showSearchAndFooterDrawers,
    showEnlargedModal,
    showQRCodeModal,
    qrCodeCheckout,
    updateViewState,
    intersectedItemsIds,
    sections,
    searchTerms,
    firstSectionOffset,
    showSettingsDrawer,
    isCheckoutEnabled,
    showStoreFavoritesModal,
    showSignUpModal,
    signUpStatusModal,
    externalPageDrawer,
  } = useContext(ViewStateContext).content;

  const { isPortraitView, isLandscapeView } = useScreenOrientation();

  const [showCheckoutDrawer, setShowCheckoutDrawer] = useState(false);
  const [isQRCheckoutSuccess, setIsQRCheckoutSuccess] = useState(false);
  const searchControllerRef = useRef(null);
  const {
    handleSearchProducts,
    isPending: isSearchPending,
    errorSearchingProducts,
  } = useContentItems(searchControllerRef);

  const { totalItems, basketItems, totalPrice } = useManageBasket();

  const { sendMessage } = useWebSocketWorkerMessage((event: MessageEvent) => {
    switch (event.data.type) {
      case SOCKET_MESSAGE_TYPE.INITIALIZE_DEVICE: {
        const deviceId = LocalStorageService.getItem(LocalStorageKeys.DeviceId);
        const tenantId = LocalStorageService.getItem(LocalStorageKeys.TenantId);
        const storeId = LocalStorageService.getItem(LocalStorageKeys.StoreId);
        const version = LocalStorageService.getItem(LocalStorageKeys.Version);
        const lastUpdateAt = LocalStorageService.getItem(
          LocalStorageKeys.LastUpdateAt
        );

        if (deviceId && tenantId && storeId) {
          const params = {
            deviceId,
            tenantId,
            storeId,
          };

          if (version) {
            params["version"] = {
              version,
              appliedAt: lastUpdateAt.replace(/"/g, ""),
            };
          }

          sendMessage({
            type: SOCKET_MESSAGE_TYPE.INITIALIZE_DEVICE,
            params,
          });
        }

        break;
      }
      case SOCKET_MESSAGE_TYPE.CHECKOUT_CONFIRMATION: {
        const status = event.data.status;

        updateViewState({
          qrCodeCheckout: {
            status,
            inProgress: status === QRCodeCheckoutStatus.InProgress,
            isFailed: status === QRCodeCheckoutStatus.Failed,
          },
          ...(status === QRCodeCheckoutStatus.Failed && {
            showQRCodeModal: false,
          }),
        });

        switch (status) {
          case QRCodeCheckoutStatus.InProgress:
            trackEvent({
              eventType: TrackingEventTypes.SummaryConfirm,
              data: {},
            });

            break;
          case QRCodeCheckoutStatus.Failed:
            trackEvent({
              eventType: TrackingEventTypes.SummaryConfirmFailed,
              data: {},
            });

            break;
          case QRCodeCheckoutStatus.Success:
            // Persisted HERE, synchronously as the message lands, rather than
            // in the effect below: a reload or sleep in the render-plus-effect
            // gap would leave a completed checkout unrecorded (ticket aecd58ce).
            //
            // Attributed to the session the QR was MINTED for, never the
            // current one: this socket is scoped to the device, not the
            // session, so a shopper who kept their QR url can pay long after
            // the device moved on — and marking the live session then would
            // throw away a good basket. No fallback for a missing record, for
            // the same reason.
            markSessionFinished(getQRCheckoutSessionId());

            setIsQRCheckoutSuccess(true);

            break;
        }

        break;
      }
    }
  });

  const handleScanSuccess = useCallback(() => {
    if (showBasketDrawer) {
      trackEvent({
        eventType: TrackingEventTypes.BasketExit,
        data: {},
      });
    }

    if (showCheckoutDrawer) {
      trackEvent({
        eventType: TrackingEventTypes.CheckoutExit,
        data: {},
      });
    }

    setShowBasketDrawer(false);
    setShowCheckoutDrawer(false);
  }, [showBasketDrawer, showCheckoutDrawer]);

  const { setScannerDisabled } = useContext(ScannerContext);
  const { showStyleNotExistModal, handleCloseModal } =
    useScanner(handleScanSuccess);

  const { checkoutConfigurations } = useManageCheckoutConfigurations();

  const contentItemsRefs = useRef({});
  const contentItemsAnchorRefs = useRef({});
  const contentRef = useRef<HTMLDivElement>(null);
  const searchDrawerRef = useRef<HTMLDivElement>(null);

  usePreloadMedia(preloadMediaItems, false);

  const handleOpenBasketDrawer = useCallback(() => {
    setShowBasketDrawer(true);
    updateViewState({ showBasketDrawer: true });

    trackEvent({
      eventType: TrackingEventTypes.BasketOpen,
      data: {},
    });
  }, [updateViewState]);

  const handleCloseBasketDrawer = useCallback(() => {
    setShowBasketDrawer(false);
    updateViewState({ showBasketDrawer: false });

    trackEvent({
      eventType: TrackingEventTypes.BasketExit,
      data: {},
    });
  }, [updateViewState]);

  const handleCloseCheckoutDrawer = useCallback(() => {
    setShowCheckoutDrawer(false);

    trackEvent({
      eventType: TrackingEventTypes.CheckoutExit,
      data: {},
    });
  }, []);

  const handleGoToCheckout = useCallback(
    (checkoutConfigurationsType: CheckoutConfigurationType) => {
      setShowBasketDrawer(false);
      setShowCheckoutDrawer(true);
      setCheckoutConfigurationsType(checkoutConfigurationsType);

      updateViewState({ showBasketDrawer: false });

      const preparedBasketItems = basketItems.map((item) => ({
        ean: item.ean,
        quantity: item.quantity,
      }));

      trackEvent({
        eventType: TrackingEventTypes.BasketConfirm,
        data: {
          items: preparedBasketItems,
          totalPrice,
          numberOfItems: basketItems.length,
        },
      });

      trackEvent({
        eventType: TrackingEventTypes.CheckoutOpen,
        data: {},
      });
    },
    [basketItems, totalPrice, updateViewState]
  );

  const handleEditCheckoutQuantity = useCallback(() => {
    setShowBasketDrawer(true);
    setShowCheckoutDrawer(false);

    updateViewState({ showBasketDrawer: true });

    trackEvent({
      eventType: TrackingEventTypes.BackToBasketFromCheckoutToEdit,
      data: {},
    });
  }, [updateViewState]);

  const handleTouchMove = useCallback(() => {
    if (showSearchAndFooterDrawers) {
      trackEvent({ eventType: TrackingEventTypes.SearchDrawerClose, data: {} });
    }

    updateViewState({ showSearchAndFooterDrawers: false });
  }, [showSearchAndFooterDrawers, updateViewState]);

  const handleScrollToTheBottom = useCallback(() => {
    const filteredSections = sections.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => intersectedItemsIds.indexOf(item.id) !== -1
      ),
    }));

    const lastIntersectedItem =
      filteredSections[filteredSections?.length - 1]?.items[
        filteredSections[filteredSections.length - 1]?.items?.length - 1
      ]?.id;

    contentItemsAnchorRefs.current[lastIntersectedItem].scrollIntoView(
      SMOOTH_END_SCROLL_OPTIONS
    );

    setTimeout(() => {
      setShowGoToBottomButton(() => false);
    }, 1000);
  }, [intersectedItemsIds, sections]);

  const handleShowDrawers = useCallback(() => {
    updateViewState({ showSearchAndFooterDrawers: true });

    trackEvent({ eventType: TrackingEventTypes.SearchDrawerOpen, data: {} });
  }, [updateViewState]);

  const handleToggleLanguageAndCurrencyModal = useCallback(() => {
    updateViewState({
      showLanguageAndCurrencyModal: !showLanguageAndCurrencyModal,
    });

    showLanguageAndCurrencyModal &&
      trackEvent({
        eventType: TrackingEventTypes.LanguageAndCurrencyModalClose,
        data: {},
      });
  }, [showLanguageAndCurrencyModal, updateViewState]);

  const handleToggleSignUpModal = useCallback(() => {
    if (!showSignUpModal) {
      updateViewState({
        showSignUpModal: true,
      });
    }
  }, [showSignUpModal, updateViewState]);

  const handleEnlargedModalClose = useCallback(() => {
    updateViewState({
      showEnlargedModal: false,
      enlargedMedias: { initialIndex: 0, medias: [] },
    });
  }, [updateViewState]);

  const handleQrCodeModalClose = useCallback(() => {
    updateViewState({
      showQRCodeModal: false,
    });
  }, [updateViewState]);

  const handleCheckoutFailedModalClose = useCallback(() => {
    updateViewState({
      qrCodeCheckout: { ...qrCodeCheckout, isFailed: false },
    });
  }, [qrCodeCheckout, updateViewState]);

  const handleSettingDrawerClose = useCallback(() => {
    updateViewState({ showSettingsDrawer: false });
  }, [updateViewState]);

  const handleSettingDrawerOpen = useCallback(() => {
    updateViewState({ showSettingsDrawer: true });
  }, [updateViewState]);

  const handleStoreFavoritesModalOpen = useCallback(() => {
    updateViewState({ showStoreFavoritesModal: true });
  }, [updateViewState]);

  const handleStoreFavoritesModalClose = useCallback(() => {
    updateViewState({
      showStoreFavoritesModal: false,
      showSearchAndFooterDrawers: false,
    });
  }, [updateViewState]);

  const handleSignUpStatusModalClose = useCallback(() => {
    updateViewState({
      signUpStatusModal: {
        isOpen: false,
        status: null,
        errorMessage: null,
      },
    });
  }, [updateViewState]);

  const handleExternalPageDrawerClose = useCallback(() => {
    updateViewState({
      externalPageDrawer: {
        isOpen: false,
        src: null,
      },
    });
  }, [updateViewState]);

  const focusedItemType = useMemo(
    () =>
      sections
        .find((section) =>
          section.items.some((item) => item.id === focusedItemId)
        )
        ?.items?.find((item) => item.id === focusedItemId)?.type,
    [sections, focusedItemId]
  );

  const showStoreFavoritesButton = useMemo(
    () => collections?.items.filter((item) => item.totalItems > 0).length > 0,
    [collections]
  );

  const topSpace = useMemo(
    () =>
      isPortraitView && showSearchAndFooterDrawers
        ? `${
            searchDrawerHeight +
            BUTTONS_CONTAINER_TOP_SCROLLED -
            BUTTON_HEIGHT -
            BUTTONS_GAP
          }px`
        : `${BUTTONS_CONTAINER_TOP_DEFAULT}px`,
    [isPortraitView, showSearchAndFooterDrawers, searchDrawerHeight]
  );

  useEffect(() => {
    if (!isQRCheckoutSuccess) {
      return;
    }

    setIsQRCheckoutSuccess(false);

    trackEvent({
      eventType: TrackingEventTypes.SummaryConfirmSuccess,
      data: {
        totalItems,
        totalPrice,
        countryCode: basketItems?.[0]?.price?.countryCode,
        currencyCode: basketItems?.[0]?.price?.currencyCode,
        basketItems: basketItems?.map((item) => ({
          ean: item.ean,
          styleId: item.productId,
          variantId: item.variantId,
        })),
      },
    });

    navigate(ROUTES.ConfirmedOrderView);
  }, [basketItems, isQRCheckoutSuccess, navigate, totalItems, totalPrice]);

  useEffect(
    () => {
      if (focusedItemId && contentItemsRefs.current[focusedItemId]) {
        if (firstSectionOffset) {
          setTimeout(
            () =>
              contentItemsRefs.current[focusedItemId].scrollIntoView(
                SMOOTH_START_SCROLL_OPTIONS
              ),
            250
          );
          return;
        }
        contentItemsRefs.current[focusedItemId].scrollIntoView(
          SMOOTH_START_SCROLL_OPTIONS
        );
      }
    },
    // this use effect supposed to scroll to the focused item only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focusedItemId, focusedItemType]
  );

  useEffect(
    () => {
      if (firstSectionOffset === 0) {
        return;
      }

      if (showSearchAndFooterDrawers && isPortraitView) {
        updateViewState({
          firstSectionOffset: searchDrawerHeight + ADDITIONAL_OFFSET,
        });
      } else {
        updateViewState({
          firstSectionOffset: 0,
        });
      }
    },
    // updateViewState should not be in dependencies because it is a setter function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      firstSectionOffset,
      showSearchAndFooterDrawers,
      searchDrawerHeight,
      isPortraitView,
    ]
  );

  useEffect(
    () => {
      if (!checkoutConfigurations) {
        return;
      }

      const isCheckoutEnabled = checkoutConfigurations.some(
        (config) => config.isEnabled
      );

      updateViewState({ isCheckoutEnabled });
    },
    // updateViewState should not be in dependencies because it is a setter function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkoutConfigurations]
  );

  useEffect(() => {
    setShowBasketDrawer(showBasketDrawerContextValue);
  }, [showBasketDrawerContextValue]);

  useEffect(() => {
    setScannerDisabled(showCheckoutDrawer);
  }, [setScannerDisabled, showCheckoutDrawer]);

  useEffect(() => {
    connect();
  }, [connect]);

  useEffect(() => {
    if (scannedValue && searchControllerRef?.current) {
      searchControllerRef?.current?.abort();
    }
  }, [scannedValue]);

  useEffect(
    () => {
      if (searchTerms.length) {
        handleSearchProducts({ newSearchTerms: searchTerms, pageNumber: 1 });
      }
    },
    // this use effect supposed to run only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    // Layout/orientation related
    isPortraitView,
    isLandscapeView,
    contentRef,
    searchDrawerRef,
    contentItemsRefs,
    contentItemsAnchorRefs,
    searchDrawerHeight,
    setSearchDrawerHeight,
    handleTouchMove,
    topSpace,

    // Search related
    handleSearchProducts,
    isSearchPending,
    errorSearchingProducts,
    sections,

    // Basket related
    showBasketDrawer,
    handleOpenBasketDrawer,
    handleCloseBasketDrawer,
    basketItemsQuantity: totalItems,

    // Checkout related
    showCheckoutDrawer,
    handleGoToCheckout,
    handleCloseCheckoutDrawer,
    handleEditCheckoutQuantity,
    checkoutConfigurationsType,
    isCheckoutEnabled,
    showQRCodeModal,
    qrCodeCheckout,
    handleQrCodeModalClose,
    handleCheckoutFailedModalClose,

    // Language/Currency related
    showLanguageAndCurrencyModal,
    handleToggleLanguageAndCurrencyModal,

    // Navigation/Scroll related
    handleScrollToTheBottom,
    setShowGoToBottomButton,
    showGoToBottomButton: showGoToBottomButton && !showSearchAndFooterDrawers,
    entryPageRoute,

    // Modals/Drawers related
    showSearchAndFooterDrawers:
      showSearchAndFooterDrawers &&
      !showLanguageAndCurrencyModal &&
      !showSignUpModal &&
      !signUpStatusModal.isOpen,
    handleShowDrawers,
    showEnlargedModal,
    handleEnlargedModalClose,
    showSettingsDrawer,
    handleSettingDrawerClose,
    handleSettingDrawerOpen,

    // Store favorites related
    handleStoreFavoritesModalOpen,
    showStoreFavoritesModal,
    handleStoreFavoritesModalClose,
    showStoreFavoritesButton,

    // Sign up related
    handleToggleSignUpModal,
    showSignUpModal,
    signUpStatusModal,
    handleSignUpStatusModalClose,

    // Other modals/utilities
    t,
    handleCloseModal,
    showStyleNotExistModal,
    externalPageDrawer,
    handleExternalPageDrawerClose,
  };
};

export default useManageContentView;
